import { gql } from "@apollo/client";
import { BaseMutationTuple, jssaQuery, useBaseMutation } from "./index";
import { CostPayoutModifier } from "../../../common/models";
import { costPayoutModifierAllFieldsFragment, costMetadataAllFieldsFragment, plprAllFieldsFragment } from "./CommonQueries";
import { CostPayoutModifierInput, CostPayoutModifierCreateInput } from "../../../common/inputs/CostPayoutModifierInput";
import { cleanObject } from "../../../common/utils";

export const allCostPayoutModifiersQuery = gql`
    ${costPayoutModifierAllFieldsFragment}
    query allCostPayoutModifiers($options: CostPayoutModifierInput) {
        allCostPayoutModifiers(options: $options) {
            ...CostPayoutModifierAllFields
        }
    }
`;

export const createCostPayoutModifierMutation = gql`
    ${costPayoutModifierAllFieldsFragment}
    mutation createCostPayoutModifier($data: CostPayoutModifierCreateInput!) {
        createCostPayoutModifier(data: $data) {
            ...CostPayoutModifierAllFields
        }
    }
`;

export const updateCostPayoutModifierMutation = gql`
    ${costPayoutModifierAllFieldsFragment}
    mutation updateCostPayoutModifier($data: CostPayoutModifierUpdateInput!) {
        updateCostPayoutModifier(data: $data) {
            ...CostPayoutModifierAllFields
        }
    }
`;

export const useCreateCostPayoutModifierMutation = (): BaseMutationTuple<CostPayoutModifierCreateInput, CostPayoutModifier> => {
    const transformInput = (input: CostPayoutModifierCreateInput) => {
        return { data: input };
    };

    const transformOutput = (output: any) => {
        return output.createCostPayoutModifier;
    };

    return useBaseMutation<CostPayoutModifierCreateInput, CostPayoutModifier>(
        createCostPayoutModifierMutation,
        transformInput,
        transformOutput,
    );
};

export const useUpdateCostPayoutModifierMutation = (): BaseMutationTuple<CostPayoutModifier, CostPayoutModifier> => {
    const transformInput = (costPayoutModifier: CostPayoutModifier) => {
        return {
            data: cleanObject(costPayoutModifier, {
                exclusions: ["createdAt", "updatedAt"]
            })
        };
    };

    const transformOutput = (output: any) => {
        return output.updateCostPayoutModifier;
    }

    return useBaseMutation<CostPayoutModifier, CostPayoutModifier>(
        updateCostPayoutModifierMutation,
        transformInput,
        transformOutput,
    );
};

export const queryAllCostPayoutModifiers = async (options?: CostPayoutModifierInput): Promise<CostPayoutModifier[]> => {
    const { data: { allCostPayoutModifiers } } = await jssaQuery({
        query: allCostPayoutModifiersQuery,
        variables: { options },
    });
    return allCostPayoutModifiers;
};

export const costPayoutModifierByIdQuery = gql`
    ${costPayoutModifierAllFieldsFragment}
    ${costMetadataAllFieldsFragment}
    ${plprAllFieldsFragment}
    query costPayoutModifierById($id: String!) {
        costPayoutModifierById(id: $id) {
            ...CostPayoutModifierAllFields
            costMetadata {
                ...CostMetadataAllFields
            }
            payoutRuleMappings {
                ...PLPRAllFields
            }
        }
    }
`;

export const queryCostPayoutModifierById = async (id: string): Promise<CostPayoutModifier | null> => {
    const { data: { costPayoutModifierById } } = await jssaQuery({
        query: costPayoutModifierByIdQuery,
        variables: { id },
        fetchPolicy: 'network-only',
    });
    return costPayoutModifierById;
};
